import { Component, DestroyRef, Inject, Optional, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { OutputConfirmProductSearch } from 'src/app/models/products/productSearch';
import { FormControl, Validators } from '@angular/forms';
import { AlertService } from 'src/app/services/alert.service';
import { OutputsCollectionsService } from 'src/app/services/outputs-collections.service';
import { ProductsService } from 'src/app/services/products.service';
import { IProduct } from 'src/app/models/products/product';
import { ProductListResult } from 'src/app/models/products/product-result';
import { DialogResult } from 'src/app/models/dialog-result';
import { FileExtension } from 'src/app/models/product-data-filename-conventions/file-extension';
import { ProductDataFilenameConventionService } from 'src/app/services/product-data-filename-conventions/product-data-filename-convention.service';
import { ProductDataFilesBlockers } from './create-outputs-collection/collection-product-data-files-selection.component';
import { ProductDataFilenameConventionSummary } from 'src/app/models/outputs-collections/review-product-data-filenames/product-data-filename-convention';
import {
  FilenameIdentifier,
  ProductDataFilenameConvention,
} from 'src/app/models/product-data-filename-conventions/product-data-filename-convention';
import {
  MediaCoverage,
  OutputFormulaSelectionChange,
  PickedFormulaMedia,
  PickerMode,
} from 'src/app/components/shared/output-formula-picker/output-formula-picker.component';
import {
  aggregateEvCoverage,
  describeEvCoverage,
  EvConventionEntry,
} from 'src/app/components/shared/output-formula-picker/coverage-helpers';
import { FilenameConventionService } from 'src/app/services/filename-conventions/filename-convention.service';
import { OutputFormula } from 'src/app/models/output-formulas/output-formula';
import { outputFormulaValueIsEvFamily } from 'src/app/models/output-formulas/output-formula-value';
import { ReviewOutputFilenamesDialogComponent } from 'src/app/components/home/output-collections/review-output-filenames-dialog/review-output-filenames-dialog.component';
import { ReviewOutputFilenamesDialogData } from 'src/app/models/outputs-collections/review-output-filenames/dialog-data';
import { ReviewOutputFilesDialogComponent } from 'src/app/components/home/output-collections/review-output-files-dialog/review-output-files-dialog.component';
import { ReviewOutputFilesDialogData } from 'src/app/models/outputs-collections/preview-output-files/dialog-data';

@Component({
    selector: 'app-create-outputs-collection',
    templateUrl: './create-outputs-collection.component.html',
    styleUrls: ['./create-outputs-collection.component.scss'],
    changeDetection: ChangeDetectionStrategy.Default,
    standalone: false,
})
export class CreateOutputsCollectionComponent {
  readonly DialogResult = DialogResult;
  isDownload = false;
  summaryForm = new FormControl<string>('', [Validators.pattern(/^[a-zA-Z0-9_\-. ]*$/)]);
  products: IProduct[] = [];

  productDataFormats: FileExtension[] = [];
  productDataBlockers: ProductDataFilesBlockers | null = null;

  formatsGeneratedInStep3: FileExtension[] = [];

  pdfcConvention: ProductDataFilenameConventionSummary | null = null;

  pdfcIdentifier: FilenameIdentifier | null = null;

  selectedFormulaIds: string[] = [];
  selectedPerMediaSelection: readonly PickedFormulaMedia[] = [];
  formulaCoverage: MediaCoverage = { coversStills: false, coversVideos: false };
  formulaCoverageSummary = 'Coverage: no formulas picked';
  isFormulaCoverageValid = false;
  readonly mode = new FormControl<PickerMode>('range', { nonNullable: true });
  evConventionEntriesById: Map<string, EvConventionEntry[]> = new Map();
  private lastSelectionChange: OutputFormulaSelectionChange | null = null;

  isCheckingFilenames = false;
  isCheckingFiles = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public dataDialog: OutputConfirmProductSearch,
    public dialogRef: MatDialogRef<CreateOutputsCollectionComponent>,
    private outputsCollectionService: OutputsCollectionsService,
    private productsService: ProductsService,
    private alertService: AlertService,
    private productDataFilenameConventionService: ProductDataFilenameConventionService,
    private dialog: MatDialog,
    private destroyRef: DestroyRef,
    @Optional() private filenameConventionService?: FilenameConventionService,
  ) {}

  ngOnInit(): void {
    this.hydrateFromWizardState();
    this.loadProducts();
    this.loadPdfcExtensions();
    this.loadEvConventionEntries();
  }

  private hydrateFromWizardState(): void {
    // Only lock the toggle when an outer picker explicitly set the mode via
    // dataDialog.mode (WTM Phase 3a Step 4 flow). Otherwise leave it editable
    // so the wizard itself is the mode authoring surface — release-collection-
    // action and other batch-triggered flows open the wizard without a carried
    // mode, and previously users had no way to reach EV mode from those paths.
    const carriedMode = this.dataDialog?.mode;
    this.mode.setValue(carriedMode ?? 'range');
    if (carriedMode !== undefined) {
      this.mode.disable();
    }
    this.selectedFormulaIds = this.dataDialog?.outputFormulaIds ?? [];
  }

  private loadPdfcExtensions(): void {
    const organizationId = this.dataDialog?.organizationId;
    if (!organizationId) {
      this.formatsGeneratedInStep3 = [];
      this.pdfcConvention = null;
      this.pdfcIdentifier = null;
      return;
    }
    this.productDataFilenameConventionService.getLatest(organizationId).subscribe({
      next: (convention) => {
        // Cast bridges duplicate FileExtension types (PDFC model: string-literal
        // union; component: enum). Runtime values identical; dedup as follow-up.
        const extensions =
          (convention?.filename?.extensions as FileExtension[] | undefined) ?? [];
        this.formatsGeneratedInStep3 = extensions;
        this.pdfcConvention = this.buildConventionSummary(convention, extensions);
        this.pdfcIdentifier = convention?.filename?.identifier ?? null;
      },
      error: () => {
        this.formatsGeneratedInStep3 = [];
        this.pdfcConvention = null;
        this.pdfcIdentifier = null;
      },
    });
  }

  // sample.pattern is synthesised — backend PDFC ships no pre-baked sample.
  private buildConventionSummary(
    convention: ProductDataFilenameConvention | null,
    extensions: FileExtension[],
  ): ProductDataFilenameConventionSummary | null {
    if (convention == null || convention.filename == null || extensions.length === 0) {
      return null;
    }
    const identifier = convention.filename.identifier;
    const sampleExtension = extensions[0];
    const samplePattern = this.buildSamplePattern(identifier, sampleExtension);
    return {
      identifier: { kind: identifier },
      extensions: { enabled: extensions },
      sample: { pattern: samplePattern },
    };
  }

  private buildSamplePattern(identifier: string, extension: FileExtension): string {
    const identifierExample = identifier === 'Barcode'
      ? '6973894500021'
      : identifier === 'Reference'
        ? 'SKU-123'
        : 'Product-Description';
    return `${identifierExample}.${extension.toLowerCase()}`;
  }

  loadProducts(): void {
    this.productsService.listProducts(this.dataDialog?.batchId ?? null).subscribe({
      next: (result: ProductListResult) => {
        this.products = result.products.filter((p) =>
          p.id != null && this.dataDialog.productIds?.includes(p.id),
        );
      },
    });
  }

  onProductDataFormatsChange(formats: FileExtension[]): void {
    this.productDataFormats = formats;
  }

  onProductDataBlockersChange(blockers: ProductDataFilesBlockers | null): void {
    this.productDataBlockers = blockers;
  }

  onFormulaSelectionChanged(event: OutputFormulaSelectionChange): void {
    this.selectedPerMediaSelection = event.perMediaSelection;
    this.selectedFormulaIds = Array.from(
      new Set(event.perMediaSelection.map((entry) => entry.formulaId)),
    );
    this.formulaCoverage = event.coverage;
    this.lastSelectionChange = event;
    this.formulaCoverageSummary = this.computeCoverageSummary(event);
    this.isFormulaCoverageValid = event.isValid;
  }

  // When the mode was locked by an outer picker (dataDialog.mode set), the
  // template's [disabled] binding blocks the toggle from firing, so this
  // handler is a no-op in that case. When the mode is editable (no outer
  // picker), we sync the FormControl so the wizard reflects the user's choice.
  onModeChanged(next: PickerMode): void {
    if (this.mode.disabled) return;
    this.mode.setValue(next);
  }

  get selectedItems(): IProduct[] {
    return this.products;
  }

  get selectedEvIds(): string[] {
    return this.selectedItems
      .map((item) => item.masterSuiteEnhancementVariantId)
      .filter((id): id is string => id != null && id !== '');
  }

  private computeCoverageSummary(event: OutputFormulaSelectionChange): string {
    if (this.mode.value !== 'ev') {
      return event.coverageSummary;
    }
    const hasItems = this.selectedEvIds.length > 0;
    const evFormula = event.formulas.find((formula) => outputFormulaValueIsEvFamily(formula.value));
    const hasFormula = evFormula != null;
    const formulaEvConventionIds = this.extractEvConventionIds(evFormula);
    const coverage = aggregateEvCoverage({
      selectedEvIds: this.selectedEvIds,
      formulaEvConventionIds,
      evConventionEntriesById: this.evConventionEntriesById,
    });
    return describeEvCoverage({
      coveredEvIds: coverage.coveredEvIds,
      missingEvIds: coverage.missingEvIds,
      hasItems,
      hasFormula,
    });
  }

  private extractEvConventionIds(formula: OutputFormula | undefined): string[] {
    if (formula == null) return [];
    const value = formula.value;
    switch (value.kind) {
      case 'evStills':
      case 'evVideos':
      case 'evBoth':
        return value.evConventionIds;
      default:
        return [];
    }
  }

  private loadEvConventionEntries(): void {
    const organizationId = this.dataDialog?.organizationId;
    if (this.filenameConventionService == null || organizationId == null || organizationId === '') {
      return;
    }
    this.filenameConventionService.list(organizationId).subscribe({
      next: (conventions) => {
        this.evConventionEntriesById = new Map(
          conventions
            .filter((convention) => convention.value.payload.mode === 'evBased')
            .map((convention) => {
              const payload = convention.value.payload;
              const entries: EvConventionEntry[] =
                payload.mode === 'evBased'
                  ? payload.entries.map((entry) => ({
                      masterSuiteEnhancementVariantId: entry.masterSuiteEnhancementVariantId,
                    }))
                  : [];
              return [convention.id, entries] as const;
            }),
        );
        this.recomputeCoverageSummary();
      },
      error: () => {
        this.evConventionEntriesById = new Map();
      },
    });
  }

  private recomputeCoverageSummary(): void {
    if (this.lastSelectionChange == null) return;
    this.formulaCoverageSummary = this.computeCoverageSummary(this.lastSelectionChange);
  }

  get isProductDataTicked(): boolean {
    return this.productDataFormats.length > 0;
  }

  get hasCategoryBlockers(): boolean {
    return this.isProductDataTicked
      && this.productDataBlockers != null
      && this.productDataBlockers.missing > 0;
  }

  get aggregateMissingCount(): number {
    return this.isProductDataTicked && this.productDataBlockers != null
      ? this.productDataBlockers.missing
      : 0;
  }

  get isFormulaCategorySelected(): boolean {
    return this.selectedFormulaIds.length > 0;
  }

  get hasAnyCategorySelected(): boolean {
    return this.productDataFormats.length > 0
      || this.isFormulaCategorySelected;
  }

  get isCreateDisabled(): boolean {
    return this.summaryForm.invalid
      || this.isDownload
      || !this.hasAnyCategorySelected
      || this.hasCategoryBlockers
      || (this.isFormulaCategorySelected && !this.isFormulaCoverageValid);
  }

  get isCheckFilenamesDisabled(): boolean {
    const hasProducts = (this.dataDialog?.productIds?.length ?? 0) > 0;
    return !hasProducts || this.selectedFormulaIds.length === 0 || this.isCheckingFilenames;
  }

  get isCheckFilesDisabled(): boolean {
    const hasProducts = (this.dataDialog?.productIds?.length ?? 0) > 0;
    return !hasProducts || this.selectedFormulaIds.length === 0 || this.isCheckingFiles;
  }

  checkFilenames(): void {
    if (this.isCheckFilenamesDisabled) return;
    this.isCheckingFilenames = true;
    this.outputsCollectionService
      .previewOutputFilenames({
        productIds: this.dataDialog.productIds ?? [],
        outputFormulaIds: this.selectedFormulaIds,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.isCheckingFilenames = false;
          const data: ReviewOutputFilenamesDialogData = { response };
          this.dialog.open(ReviewOutputFilenamesDialogComponent, { data, width: '800px' });
        },
        error: () => {
          this.isCheckingFilenames = false;
          this.alertService.errorToast('Failed to load filename preview.');
        },
      });
  }

  checkFiles(): void {
    if (this.isCheckFilesDisabled) return;
    this.isCheckingFiles = true;
    this.outputsCollectionService
      .previewOutputFiles({
        productIds: this.dataDialog.productIds ?? [],
        outputFormulaIds: this.selectedFormulaIds,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.isCheckingFiles = false;
          const data: ReviewOutputFilesDialogData = { response };
          this.dialog.open(ReviewOutputFilesDialogComponent, { data, width: '800px' });
        },
        error: () => {
          this.isCheckingFiles = false;
          this.alertService.errorToast('Failed to load files preview.');
        },
      });
  }

  create(): void {
    this.outputsCollectionService
      .createOutputsCollection({
        productIds: this.dataDialog.productIds || [],
        outputFormulaIds: this.selectedFormulaIds,
        organizationId: this.dataDialog.organizationId!,
        batchId: this.dataDialog.batchId!,
        summary: this.summaryForm.value!,
        productData: { formats: this.productDataFormats },
      })
      .subscribe({
        next: () => {
          this.isDownload = false;
          this.alertService.successToast('Success! the collection will finish exporting shortly!');
          this.dialogRef.close(DialogResult.Success);
        },
        error: () => {
          this.isDownload = false;
        },
      });
  }
}
